/**
 * `ActiveIndicator` — the one accent bar that MOVES between navigation items
 * instead of being redrawn on each.
 *
 * Render it inside the active item only; framer's `layoutId` carries it from
 * the previous item's position to the new one with the indicator spring. Wrap
 * the nav in `<IndicatorGroup id="…">` so two navs on one screen do not share
 * a bar.
 *
 *   {active && <ActiveIndicator id="rail" className="accent" />}
 */
import React from 'react';
import { LayoutGroup, motion } from 'framer-motion';
import { T } from './presets';

interface ActiveIndicatorProps {
  id: string;
  className?: string;
  style?: React.CSSProperties;
  /** `data-*` hooks a test can read — e.g. `data-active-indicator`, which the
   *  e2e rail check counts inside the `aria-current` item. */
  [dataAttribute: `data-${string}`]: string | boolean | undefined;
}

export const ActiveIndicator: React.FC<ActiveIndicatorProps> = ({
  id,
  className,
  style,
  ...rest
}) => (
  <motion.span
    layoutId={id}
    className={className}
    style={style}
    transition={T.indicator}
    aria-hidden="true"
    {...rest}
  />
);

interface IndicatorGroupProps {
  id: string;
  children: React.ReactNode;
}

export const IndicatorGroup: React.FC<IndicatorGroupProps> = ({
  id,
  children,
}) => <LayoutGroup id={id}>{children}</LayoutGroup>;
